import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Loader2, ShieldCheck, LogOut, Crown, MapPin, X } from "lucide-react";

function daysLeft(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
}

export default function PricingPage() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [durations, setDurations] = useState([]);
  const [durationKey, setDurationKey] = useState("monthly");
  const [cityOptions, setCityOptions] = useState([]);
  const [busy, setBusy] = useState("");
  const [picker, setPicker] = useState(null); // {plan}
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    api.get("/billing/pricing").then(({ data }) => { setPlans(data.plans); setDurations(data.durations); });
    api.get("/billing/cities-options").then(({ data }) => setCityOptions(data)).catch(() => {});
    refresh();
  }, []);

  const startCheckout = async (planId, cities = []) => {
    setBusy(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId, origin_url: window.location.origin, cities, duration_key: durationKey,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível iniciar o pagamento");
      setBusy("");
    }
  };

  const onSubscribe = (plan) => {
    if (plan.cities_limit == null) {
      startCheckout(plan.id, []);
    } else {
      setSelected([]);
      setPicker(plan);
    }
  };

  const toggleCity = (c) => {
    setSelected((prev) => {
      if (prev.includes(c)) return prev.filter((x) => x !== c);
      if (prev.length >= picker.cities_limit) return prev;
      return [...prev, c];
    });
  };

  const confirmCities = () => {
    if (selected.length < 1) return;
    const plan = picker;
    setPicker(null);
    startCheckout(plan.id, selected);
  };

  const trialActive = user?.is_active && user?.trial;
  const left = daysLeft(user?.access_expires_at);

  return (
    <div className="min-h-screen bg-[#09090b]">
      <header className="border-b border-[#27272a] px-6 lg:px-12 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/radarx-logo.png" alt="RadarX" className="h-9 w-auto" />
          <span className="label-tiny text-[#71717a] hidden sm:inline">Subscrição</span>
        </div>
        <div className="flex items-center gap-4">
          {user?.is_active && (
            <button onClick={() => navigate("/")} className="text-sm text-[#a1a1aa] hover:text-[#fafafa]" data-testid="go-dashboard">
              Ir para o painel
            </button>
          )}
          <button onClick={logout} className="text-sm text-[#a1a1aa] hover:text-[#ef4444] flex items-center gap-1.5" data-testid="pricing-logout">
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-3">
          <h1 className="font-head text-4xl sm:text-5xl font-light text-[#fafafa]">
            Escolhe o teu <span className="text-[#c8aa6e]">acesso</span>
          </h1>
          <p className="text-[#a1a1aa] mt-3 text-sm max-w-xl mx-auto">
            Paga apenas pelas cidades que te interessam. Quanto mais cidades, mais dados.
          </p>
        </div>

        {trialActive ? (
          <div className="text-center mb-10">
            <span className="inline-block text-xs text-[#c8aa6e] border border-[#c8aa6e]/40 bg-[#c8aa6e]/10 px-4 py-2">
              Período de teste · {left} {left === 1 ? "dia restante" : "dias restantes"}
            </span>
          </div>
        ) : (
          !user?.is_active && (
            <div className="text-center mb-10">
              <span className="inline-block text-xs text-[#ef4444] border border-[#ef4444]/40 bg-[#ef4444]/10 px-4 py-2" data-testid="trial-expired">
                O teu acesso terminou. Subscreve para continuar.
              </span>
            </div>
          )
        )}

        {durations.length > 0 && (
          <div className="flex justify-center mb-2" data-testid="duration-selector">
            <div className="inline-flex flex-wrap justify-center border border-[#27272a] bg-[#18181b]">
              {durations.map((d) => (
                <button key={d.key} onClick={() => setDurationKey(d.key)} data-testid={`duration-${d.key}`}
                  className={`px-4 py-2.5 text-sm border-r border-[#27272a] last:border-r-0 transition-colors ${durationKey === d.key ? "bg-[#c8aa6e] text-black" : "text-[#a1a1aa] hover:text-[#fafafa]"}`}>
                  {d.label}
                  {d.discount_percent > 0 && (
                    <span className={`ml-1.5 text-[10px] ${durationKey === d.key ? "text-black/70" : "text-[#c8aa6e]"}`}>-{d.discount_percent}%</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {plans.map((p) => {
            const featured = p.id === "city3";
            const activeDur = durations.find((d) => d.key === durationKey);
            const pr = activeDur?.prices?.[p.id];
            return (
              <div key={p.id} data-testid={`plan-${p.id}`}
                className={`relative bg-[#18181b] border p-6 flex flex-col ${featured ? "border-[#c8aa6e]" : "border-[#27272a]"}`}>
                {featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#c8aa6e] text-black text-[10px] uppercase tracking-[0.2em] px-3 py-1 flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Popular
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#c8aa6e]" />
                  <h3 className="font-head text-xl text-[#fafafa]">{p.name}</h3>
                </div>
                <div className="mt-4 mb-1 flex items-end gap-1" data-testid={`plan-price-${p.id}`}>
                  <span className="font-head text-4xl font-light text-[#c8aa6e]">{pr ? pr.per_month : p.amount}€</span>
                  <span className="text-sm text-[#71717a] mb-1.5">/mês</span>
                </div>
                <p className="text-xs text-[#71717a] mb-1" data-testid={`plan-total-${p.id}`}>
                  {activeDur && activeDur.months > 1
                    ? <>Total {pr?.total}€ · {activeDur.label.toLowerCase()}{pr?.saving > 0 ? <span className="text-[#c8aa6e]"> · poupa {pr.saving}€</span> : null}</>
                    : "Faturado mensalmente"}
                </p>
                <p className="text-xs text-[#71717a] mb-3">
                  {p.cities_limit == null ? "Acesso ilimitado a todas as cidades"
                    : `Escolhe ${p.cities_limit} ${p.cities_limit === 1 ? "cidade" : "cidades"}`}
                </p>
                <ul className="mt-2 space-y-2.5 flex-1">
                  {(p.features || []).map((f, i) => (
                    <li key={`${f}-${i}`} className="flex items-start gap-2 text-sm text-[#a1a1aa]">
                      <Check className="w-4 h-4 text-[#c8aa6e] shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <button data-testid={`checkout-${p.id}`} onClick={() => onSubscribe(p)} disabled={!!busy}
                  className={`mt-6 px-4 py-2.5 font-medium text-sm transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60 ${featured ? "bg-[#c8aa6e] text-black hover:bg-[#d4b982]" : "border border-[#3f3f46] text-[#fafafa] hover:border-[#c8aa6e]"}`}>
                  {busy === p.id && <Loader2 className="w-4 h-4 spin" />}
                  Subscrever
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-[#52525b] mt-8">Pagamento seguro via Stripe · Modo de teste</p>
      </div>

      {/* City picker modal */}
      {picker && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="city-picker">
          <div className="bg-[#18181b] border border-[#3f3f46] w-full max-w-md">
            <div className="px-5 py-4 border-b border-[#27272a] flex items-center justify-between">
              <div>
                <h3 className="font-head text-lg text-[#fafafa]">{picker.name}</h3>
                <p className="text-xs text-[#71717a]">Seleciona {picker.cities_limit} {picker.cities_limit === 1 ? "cidade" : "cidades"} ({selected.length}/{picker.cities_limit})</p>
              </div>
              <button onClick={() => setPicker(null)} className="text-[#71717a] hover:text-[#fafafa]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-2 max-h-72 overflow-y-auto">
              {cityOptions.map((c) => {
                const on = selected.includes(c);
                const disabled = !on && selected.length >= picker.cities_limit;
                return (
                  <button key={c} data-testid={`city-opt-${c}`} onClick={() => toggleCity(c)} disabled={disabled}
                    className={`px-3 py-2 text-sm border text-left transition-colors ${on ? "border-[#c8aa6e] bg-[#c8aa6e]/10 text-[#c8aa6e]" : disabled ? "border-[#27272a] text-[#52525b]" : "border-[#3f3f46] text-[#a1a1aa] hover:border-[#c8aa6e]"}`}>
                    <span className="inline-flex items-center gap-1.5">{on && <Check className="w-3 h-3" />}{c}</span>
                  </button>
                );
              })}
            </div>
            <div className="px-5 py-4 border-t border-[#27272a] flex justify-end">
              <button data-testid="confirm-cities" onClick={confirmCities} disabled={selected.length < 1 || !!busy}
                className="bg-[#c8aa6e] text-black hover:bg-[#d4b982] px-4 py-2.5 text-sm font-medium disabled:opacity-50 flex items-center gap-2">
                {busy && <Loader2 className="w-4 h-4 spin" />} Continuar para pagamento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
